import {
  Box,
  Divider,
  Flex,
  Form,
  MonetaryInputField,
  SelectField,
  SubmitButton,
  Text,
} from '@/components';
import {
  CurrentFinalizedAmountFormSchema,
  ManagementExpenseSubsidyMonetizationStandardModifier,
  managementExpenseSubsidyMonetizationStandardType,
} from '@/features/project/projects/_models/segment-expenditure-and-execution';
import { useForm } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type FormValues = z.infer<typeof CurrentFinalizedAmountFormSchema>;

type Props = {
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

export const CurrentFinalizedAmountForm: FC<Props> = ({
  initialValues,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(CurrentFinalizedAmountFormSchema),
  });

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <Flex vertical gap={16}>
        <Text variant="h5" text={t('current-year-ordinary-revenue')} />

        <MonetaryInputField
          name="revenueInitialBudgetAmount"
          label={t('initial-budget-management-expense-grant')}
          control={control}
          errors={errors}
          unit={unit}
        />
        <MonetaryInputField
          name="revenueSupplementary1BudgetAmount"
          label={t('revenue-supplementary1-management-expense-grant')}
          control={control}
          errors={errors}
          unit={unit}
        />
        <MonetaryInputField
          name="revenueSupplementary2BudgetAmount"
          label={t('revenue-supplementary2-management-expense-grant')}
          control={control}
          errors={errors}
          unit={unit}
        />
        <MonetaryInputField
          name="revenueSupplementary3BudgetAmount"
          label={t('revenue-supplementary3-management-expense-grant')}
          control={control}
          errors={errors}
          unit={unit}
        />
        <MonetaryInputField
          name="revenueSupplementary4BudgetAmount"
          label={t('revenue-supplementary4-management-expense-grant')}
          control={control}
          errors={errors}
          unit={unit}
        />
        <MonetaryInputField
          name="revenueSupplementary5BudgetAmount"
          label={t('revenue-supplementary5-management-expense-grant')}
          control={control}
          errors={errors}
          unit={unit}
        />
        <SelectField
          name="revenueManagementExpenseSubsidyMonetizationStandards"
          label={t('management-expense-grant-of-monetization-criteria')}
          hint={t('hint-revenue-recognition-basis')}
          control={control}
          errors={errors}
          mode="multiple"
          options={managementExpenseSubsidyMonetizationStandardType.options.map(
            (value) => ({
              value,
              label:
                ManagementExpenseSubsidyMonetizationStandardModifier[value],
            }),
          )}
          required
        />
        <Divider style={{ borderWidth: '2px', marginTop: 0 }} />
        <MonetaryInputField
          labelSize="large"
          name="expenditureBudgetAmount"
          label={t('current-year-ordinary-expenses')}
          control={control}
          errors={errors}
          unit={unit}
        />

        <Divider style={{ borderWidth: '2px', marginTop: 0 }} />
        <Text variant="h5" text={t('next-year-ordinary-revenue')} />
        <MonetaryInputField
          name="revenueEstimatedAmountForNextYear"
          label={t('management-expense-grant')}
          hint={t('hint-next-year-revenue')}
          control={control}
          errors={errors}
          unit={unit}
        />
      </Flex>
      <Box marginTop={12}>
        <SubmitButton
          text={t('save')}
          loading={isSubmitting}
          disabled={!isDirty}
        />
      </Box>
    </Form>
  );
};
